import { useState } from "react";
import type { EventType } from "../types";
import { EVENT_TYPE_LABELS } from "../types";
import { createInquiry } from "../eventsApi";
import type { Inquiry } from "../types";

type Props = {
  onClose: () => void;
  onCreated: (inquiry: Inquiry) => void;
};

export function AddInquiryModal({ onClose, onCreated }: Props) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [groupSize, setGroupSize] = useState("");
  const [eventType, setEventType] = useState<EventType>("other");
  const [durationHours, setDurationHours] = useState("2");
  const [preferredDate, setPreferredDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = firstName.trim() && lastName.trim() && email.trim() && groupSize;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      const res = await createInquiry({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        group_size: Number(groupSize),
        event_type: eventType,
        duration_hours: Number(durationHours) || 2,
        preferred_dates: preferredDate ? [{ date: preferredDate, ...(startTime ? { time: startTime } : {}) }] : [],
        special_requests: specialRequests.trim() || null,
        source: "manual",
      });
      onCreated(res.data);
    } catch (err) {
      setError((err as Error).message || "Failed to create inquiry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-md mx-4 rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <h2 className="text-base font-semibold text-white">Add Event</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-tribal-muted hover:text-white text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-tribal-muted mb-1">First Name *</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                placeholder="First"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs text-tribal-muted mb-1">Last Name *</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                placeholder="Last"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-tribal-muted mb-1">Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              placeholder="email@example.com"
            />
          </div>

          <div>
            <label className="block text-xs text-tribal-muted mb-1">Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-tribal-muted mb-1">Group Size *</label>
              <input
                type="number"
                min="1"
                value={groupSize}
                onChange={(e) => setGroupSize(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                placeholder="10"
              />
            </div>
            <div>
              <label className="block text-xs text-tribal-muted mb-1">Hours</label>
              <input
                type="number"
                min="1"
                step="0.5"
                value={durationHours}
                onChange={(e) => setDurationHours(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-tribal-muted mb-1">Date</label>
              <input
                type="date"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-tribal-muted mb-1">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-tribal-muted mb-1">Event Type</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value as EventType)}
              className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none"
            >
              {(Object.entries(EVENT_TYPE_LABELS) as [EventType, string][]).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-tribal-muted mb-1">Notes</label>
            <textarea
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              rows={2}
              className="w-full resize-none rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              placeholder="Any special requests or notes…"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-400">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm text-tribal-muted hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || saving}
              className="rounded-md bg-tribal-red px-5 py-2 text-sm font-medium text-white hover:bg-tribal-red-dark disabled:opacity-50"
            >
              {saving ? "Adding…" : "Add Event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
